import type { CSSProperties } from 'react';
import clsx from 'clsx';

// Decorative aerial-view pitch outline — README.md §5 hero atmosphere note
// ("SVG-паттерн футбольного поля в правом нижнем углу"). One linework SVG,
// stroke = currentColor so the caller picks the tint via Tailwind (`text-*`)
// + opacity. The component itself is pointer-events: none and aria-hidden.
//
// Coordinates target a 600×800 portrait aerial view. Lines: outer border,
// halfway line, centre circle + spot, both penalty boxes with goal boxes,
// arcs and penalty spots. `vectorEffect: non-scaling-stroke` keeps line width
// constant regardless of how the SVG is scaled.

interface FootballPitchProps {
  className?: string;
  style?: CSSProperties;
}

export function FootballPitch({ className, style }: FootballPitchProps) {
  return (
    <svg
      className={clsx('pointer-events-none', className)}
      style={style}
      viewBox="0 0 600 800"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      vectorEffect="non-scaling-stroke"
      aria-hidden
    >
      <rect x="20" y="20" width="560" height="760" />

      {/* Halfway line + centre circle + spot */}
      <line x1="20" y1="400" x2="580" y2="400" />
      <circle cx="300" cy="400" r="80" />
      <circle cx="300" cy="400" r="3" fill="currentColor" />

      {/* Top penalty area + goal box + arc + spot */}
      <rect x="120" y="20" width="360" height="130" />
      <rect x="210" y="20" width="180" height="50" />
      <path d="M 220 150 a 80 80 0 0 0 160 0" />
      <circle cx="300" cy="100" r="3" fill="currentColor" />

      {/* Bottom penalty area + goal box + arc + spot */}
      <rect x="120" y="650" width="360" height="130" />
      <rect x="210" y="730" width="180" height="50" />
      <path d="M 220 650 a 80 80 0 0 1 160 0" />
      <circle cx="300" cy="700" r="3" fill="currentColor" />

      {/* Players — each one drifts on its own 12s patrol pattern; `data-player`
          attribute selects the per-player keyframes in index.css. Peak (scale 1.45
          + opacity 1) is timed to the ball's arrival at this player. */}
      <g className="atm-pitch-players">
        <circle data-player="p1" cx="200" cy="650" r="10" fill="currentColor" />
        <circle data-player="p2" cx="400" cy="650" r="10" fill="currentColor" />
        <circle data-player="p3" cx="300" cy="500" r="10" fill="currentColor" />
        <circle data-player="p4" cx="200" cy="350" r="10" fill="currentColor" />
        <circle data-player="p5" cx="400" cy="350" r="10" fill="currentColor" />
      </g>

      {/* Ball — passes around the perimeter P1 → P2 → P5 → P4 → P1 every 12s.
          Starts at P1 (200,650); CSS keyframes translate to deltas of the next spot. */}
      <circle className="atm-pitch-ball" cx="200" cy="650" r="7" fill="currentColor" />
    </svg>
  );
}
